/**
 * The wastage screen's slice of the API. Same shape as `../api.ts` — bearer
 * token on every call, an ApiError carrying the status — kept here so the
 * shared client stays the shared client.
 */

import type { WastageKind, WastageUnit } from "@baros/core";
import { request as shared, type Me } from "../api.js";


/** A stored `WASTE#` item, as the list route returns it. */
export interface WastageEntry {
  readonly wasteId: string;
  readonly at: string;
  readonly reason: string;
  readonly kind: WastageKind;
  readonly ingredientId: string | null;
  readonly specId: string | null;
  readonly qty: number;
  readonly unit: WastageUnit;
  readonly ml: number;
  readonly costDollars: number;
  readonly note: string;
  readonly byUserId: string;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
}

export interface WastageSummaryResponse {
  readonly count: number;
  readonly costDollars: number;
  readonly byReason: readonly { readonly reason: string; readonly costDollars: number }[];
}

export interface WastageList {
  readonly wastage: readonly WastageEntry[];
  readonly summary: WastageSummaryResponse;
}

export interface NewWastage {
  readonly reason: string;
  readonly kind: WastageKind;
  readonly ingredientId?: string | null;
  readonly specId?: string | null;
  readonly qty: number;
  readonly unit: WastageUnit;
  readonly note: string;
}

async function request<T>(
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const venuePath = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}/wastage`;

export const wastageApi = {
  /**
   * Everything recent, newest first. The route takes `from`/`to`, but a
   * venue's wastage is small (§5) and the screen wants to know which weeks
   * have anything in them, so it reads the lot and filters in the browser.
   */
  list: (token: string, venueId: string) => request<WastageList>(token, "GET", venuePath(venueId)),
  log: (token: string, venueId: string, body: NewWastage) =>
    request<{ wastage: WastageEntry }>(token, "POST", venuePath(venueId), body),
  approve: (token: string, venueId: string, wasteId: string) =>
    request<{ wastage: WastageEntry }>(
      token,
      "PUT",
      `${venuePath(venueId)}/${encodeURIComponent(wasteId)}/approve`,
    ),
  remove: (token: string, venueId: string, wasteId: string) =>
    request<{ deleted: string }>(
      token,
      "DELETE",
      `${venuePath(venueId)}/${encodeURIComponent(wasteId)}`,
    ),
};

/**
 * The prototype's `REASONS`, used only when the org has none configured.
 *
 * The reasons are tenant settings on `ORG#/META` (architecture §4 — anything
 * the prototype hardcodes is a candidate for config), and the seed writes
 * exactly this list. The fallback keeps the screen usable for an org created
 * before the setting existed rather than showing no buttons at all.
 */
export const DEFAULT_REASONS: readonly string[] = [
  "Spill",
  "Breakage",
  "Comp",
  "Staff drink",
  "Training",
  "Expired",
  "Returned",
];

/**
 * The org's wastage reasons.
 *
 * `/v1/me` returns an empty list for an org that has configured none, which
 * is the one case `DEFAULT_REASONS` is for.
 */
export function wastageReasons(me: Me): readonly string[] {
  const configured = me.org.settings.wastageReasons;
  return configured.length > 0 ? configured : DEFAULT_REASONS;
}
